import { useState } from 'react';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

/**
 * Hook personalizado para gestión de asignación de vendedores
 * @param {Object} detallesLead - Detalles del lead actual
 * @param {Function} setDetallesLead - Actualiza los detalles del lead
 * @param {Array} contactos - Lista de contactos
 * @param {Function} setContactos - Actualiza la lista de contactos
 * @param {Array} listaJsonLeadsVinculados - Lista de leads vinculados
 * @param {Function} GuardarComentario - Función para guardar comentarios
 * @param {Function} guardarNotificacion - Función para guardar notificaciones
 * @param {Function} crearObjAction - Función para crear objeto de acción
 * @returns {Object} Funciones y estados para gestión de asignación
 */
export const useLeadAssignment = (
    detallesLead,
    setDetallesLead,
    contactos,
    setContactos,
    listaJsonLeadsVinculados,
    GuardarComentario,
    guardarNotificacion,
    crearObjAction
) => {
    const { token, setToken, idUsuario, nombreCompleto } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();

    const [habilitarBtnLiberarLead, setHabilitarBtnLiberarLead] = useState(true);
    const [habilitarBtnContinuarLiberarLead, setHabilitarBtnContinuarLiberarLead] = useState(true);
    const [habilitarBtnAsignarLead, setHabilitarBtnAsignarLead] = useState(true);
    const [habilitarBtnGuardarComentario, setHabilitarBtnGuardarComentario] = useState(true);
    const [comentarioCambioVendedor, setComentarioCambioVendedor] = useState('');

    /**
     * Libera un lead y sus leads vinculados
     * @param {Function} setShowModalLiberarLeads - Cerrar modal de confirmación
     * @param {number} idLead - ID del lead a liberar
     */
    const liberarLead = async (setShowModalLiberarLeads, idLead) => {
        setHabilitarBtnLiberarLead(false);
        setHabilitarBtnContinuarLiberarLead(false);

        const jDataSend = {
            idLead: detallesLead.idLead
        };

        const response = await fetch("/api/leads/liberarLead/", {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(jDataSend)
        }).catch((error) => {
            showErrorToast('Hubo un problema con la peticion Fetch:' + error);
        });

        if (response.ok) {
            const data = await response.json();

            if (data.iResponseType == 1) {
                setHabilitarBtnLiberarLead(true);
                setHabilitarBtnContinuarLiberarLead(true);
                detallesLead.bRelease = true;

                GuardarComentario("El Lead ha sido liberado", 2, detallesLead.idLead);

                const objToActionString = crearObjAction(1, '', 1, detallesLead.idLead);
                guardarNotificacion(
                    "Se liberó Lead",
                    "<strong>" + nombreCompleto + "</strong> libero el lead <strong>#" + detallesLead.idLead + "</strong>",
                    0,
                    objToActionString,
                    1
                );

                if (contactos !== null && typeof contactos !== 'undefined') {
                    const currentLeadIndex = contactos.findIndex((lead) => lead.idLead === detallesLead.idLead);

                    if (currentLeadIndex == -1) {
                        setContactos([{ ...detallesLead }, ...contactos]);
                    } else {
                        const updatedContactos = [...contactos];
                        updatedContactos[currentLeadIndex] = { ...updatedContactos[currentLeadIndex], iDaysDiff: 0 };
                        setContactos(updatedContactos);
                    }

                    if (listaJsonLeadsVinculados.length > 0) {
                        listaJsonLeadsVinculados.forEach((lead) => {
                            GuardarComentario("El Lead ha sido liberado", 2, lead.idLead);
                            const leadIndex = contactos.findIndex((l) => l.idLead === idLead);

                            if (leadIndex == -1) {
                                setContactos([detallesLead, ...contactos]);
                            } else {
                                contactos[leadIndex].iDaysDiff = 0;
                            }
                        });
                    }
                }

                setShowModalLiberarLeads(false);
            } else {
                setHabilitarBtnLiberarLead(true);
                setHabilitarBtnContinuarLiberarLead(true);
                showErrorToast(data.vMessage);
            }
        } else {
            setHabilitarBtnLiberarLead(true);
            setHabilitarBtnContinuarLiberarLead(true);
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                window.location.href = "";
            }
        }
    };

    /**
     * Cambia el vendedor asignado a un lead
     * @param {Function} setShowModalComentarioCambioVendedor - Cerrar modal
     * @param {Array} listaLeadsLiberados - Lista completa de leads liberados
     * @param {Function} setListaLeadsLiberados - Actualizar lista de leads liberados
     * @param {Array} lista3LeadsLiberados - Lista resumida de leads liberados
     * @param {Function} mostrarLeads - Función para actualizar vista de leads
     * @param {Array} Filter_Tags - Filtros de tags activos
     * @param {boolean} filtroEventosVencidos - Filtro de eventos vencidos
     */
    const cambiarVendedorLead = async (
        setShowModalComentarioCambioVendedor,
        listaLeadsLiberados,
        setListaLeadsLiberados,
        lista3LeadsLiberados,
        mostrarLeads,
        Filter_Tags,
        filtroEventosVencidos
    ) => {
        if (!comentarioCambioVendedor) {
            showErrorToast("El mensaje es requerido");
            return;
        }

        setHabilitarBtnAsignarLead(false);
        setHabilitarBtnGuardarComentario(false);

        const jDataSend = {
            idUsuario: idUsuario,
            idLead: detallesLead.idLead
        };

        const response = await fetch("/api/leads/cambiarVendedorLead/", {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(jDataSend)
        }).catch((error) => {
            showErrorToast('Hubo un problema con la peticion Fetch:' + error);
        });

        if (response.ok) {
            const data = await response.json();

            if (data.iResponseType == 1) {
                const detalles = JSON.parse(data.detalles);

                await GuardarComentario(
                    "<b class='badge badge-soft-success' >Cambio  de vendedor</b> <b>" + detallesLead.vNombreUsuario + "</b> a <b> " + detalles.vNombreUsuario + " </b>",
                    2,
                    detallesLead.idLead
                );
                await GuardarComentario(comentarioCambioVendedor, 1, detallesLead.idLead);
                detallesLead.bRelease = false;

                if (idUsuario != detallesLead.idUsuario) {
                    const objToAction = crearObjAction(1, '', 1, detallesLead.idLead);
                    guardarNotificacion(
                        "Asignación de lead",
                        "<strong>" + nombreCompleto + "</strong> se asignó el lead <strong>#" + detallesLead.idLead + "</strong>",
                        detallesLead.idUsuario,
                        objToAction,
                        2
                    );
                }

                // Actualizar contactos
                if (contactos !== null && typeof contactos !== 'undefined') {
                    const currentLeadIndex = contactos.findIndex((lead) => lead.idLead === detallesLead.idLead);

                    if (currentLeadIndex > -1) {
                        const updatedContactos = [...contactos];
                        updatedContactos[currentLeadIndex] = { ...updatedContactos[currentLeadIndex], iDaysDiff: 0, vNombreUsuario: detalles.vNombreUsuario, idUsuario: detalles.idUsuario, vUsuario: detalles.vUsuario };
                        setContactos(updatedContactos);
                    }
                }

                // Actualizar leads vinculados
                if (listaJsonLeadsVinculados.length > 0) {
                    listaJsonLeadsVinculados.forEach((lead) => {
                        GuardarComentario(
                            "<b class='badge badge-soft-success' >Cambio  de vendedor</b> <b>" + detallesLead.vNombreUsuario + "</b> a <b> " + detalles.vNombreUsuario + " </b>",
                            2,
                            lead.idLead
                        );
                        GuardarComentario(comentarioCambioVendedor, 1, lead.idLead);

                        if (contactos !== null && contactos !== undefined) {
                            const currentLeadVinculadoIndex = contactos.findIndex((l) => lead.idLead === l.idLead);

                            if (currentLeadVinculadoIndex > -1) {
                                contactos[currentLeadVinculadoIndex].iDaysDiff = 0;
                                contactos[currentLeadVinculadoIndex].vNombreUsuario = detalles.vNombreUsuario;
                                contactos[currentLeadVinculadoIndex].idUsuario = detalles.idUsuario;
                                contactos[currentLeadVinculadoIndex].vUsuario = detalles.vUsuario;
                            }
                        }
                    });
                }

                setDetallesLead({
                    ...detallesLead,
                    vNombreUsuario: detalles.vNombreUsuario,
                    idUsuario: detalles.idUsuario,
                    vUsuario: detalles.vUsuario
                });

                // Limpiar de lista de leads liberados
                if (listaLeadsLiberados !== null && listaLeadsLiberados !== undefined) {
                    const currentLeadLiberadoIndex = listaLeadsLiberados.findIndex((l) => l.idLead === detallesLead.idLead);

                    if (currentLeadLiberadoIndex > -1) {
                        const newLeadsLiberados = [
                            ...listaLeadsLiberados.slice(0, currentLeadLiberadoIndex),
                            ...listaLeadsLiberados.slice(currentLeadLiberadoIndex + 1)
                        ];
                        setListaLeadsLiberados(newLeadsLiberados);
                    }
                }

                if (lista3LeadsLiberados !== null && lista3LeadsLiberados !== undefined) {
                    const currentLeadLiberadoIndex = lista3LeadsLiberados.findIndex((l) => l.idLead === detallesLead.idLead);

                    if (currentLeadLiberadoIndex > -1) {
                        if (typeof mostrarLeads === 'function' && Array.isArray(Filter_Tags) && typeof filtroEventosVencidos === 'boolean') {
                            mostrarLeads(Filter_Tags, filtroEventosVencidos);
                        }
                    }
                }

                showSuccessToast("Asignación de vendedor exitoso");
                setHabilitarBtnAsignarLead(true);
                setHabilitarBtnGuardarComentario(true);
                setShowModalComentarioCambioVendedor(false);
                setComentarioCambioVendedor('');
            } else {
                setHabilitarBtnAsignarLead(true);
                setHabilitarBtnGuardarComentario(true);
                showErrorToast(data.vMessage);
            }
        } else {
            setHabilitarBtnAsignarLead(true);
            setHabilitarBtnGuardarComentario(true);
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                window.location.href = "";
            }
        }
    };

    return {
        habilitarBtnLiberarLead,
        setHabilitarBtnLiberarLead,
        habilitarBtnContinuarLiberarLead,
        setHabilitarBtnContinuarLiberarLead,
        habilitarBtnAsignarLead,
        setHabilitarBtnAsignarLead,
        habilitarBtnGuardarComentario,
        setHabilitarBtnGuardarComentario,
        comentarioCambioVendedor,
        setComentarioCambioVendedor,
        liberarLead,
        cambiarVendedorLead
    };
};
